<script setup lang="ts">
/**
 * The house at a glance — an app-level RIGHT drawer, a sibling of the user and
 * status drawers.
 *
 * It began as the BIM layout's own left drawer (owner, 2026-10-19: *"could we
 * have a side drawer (left side) with widgets: clock, weather, site (alarms),
 * scenes, modes heaten, shades.."*). The owner then asked for it on every page,
 * on the right, opened from the top bar, so the BIM layout no longer owns it.
 *
 * The cards are the dashboard's own widgets, hosted in a narrow COLUMN
 * (`WIDGET_LAYOUT_TYPE`), which is why the clock measures itself against its
 * container instead of a tile height. Each card keeps its own data loading and
 * click-through; the drawer only arranges them.
 *
 * Owner (2026-10-29): *"we want cameras, then security, then Lights, heating and
 * shading remove here.. we'll add some later"* — so heating and the shade quick
 * controls are gone, cameras and the lights card arrived, and the clock and
 * weather stay above them.
 */
import { computed, defineAsyncComponent, onMounted, onUnmounted, provide, ref } from 'vue';
import { tt } from '@/utils/consoleText';
import { useUIStore } from '@/stores/ui';
import { useBottomNavMode } from '@/composables/useWideViewport';
import { useEntitiesStore } from '@/stores/entities';
import { useDashboardStore } from '@/stores/dashboard';
import type { TimeWidgetConfig } from '@/components/dashboard/widgets/clock/timeWidgetConfig';
import {
  WIDGET_LAYOUT_TYPE,
  type DashboardWidgetLike,
  type WidgetConfigLike,
} from '@/components/dashboard/dashboardTypes';

const TimeCard = defineAsyncComponent(() => import('@/components/dashboard/widgets/TimeCard.vue'));
const WeatherCard = defineAsyncComponent(() => import('@/components/dashboard/widgets/WeatherCard.vue'));
const CameraListCard = defineAsyncComponent(() => import('@/components/dashboard/widgets/CameraListCard.vue'));
const SecurityModeCard = defineAsyncComponent(() => import('@/components/dashboard/widgets/SecurityModeCard.vue'));
const LightsSummaryCard = defineAsyncComponent(() => import('@/components/dashboard/widgets/LightsSummaryCard.vue'));

const ui = useUIStore();

const open = computed(() => ui.siteDrawerOpen);

function close() {
  ui.closeSiteDrawer();
}

// ── Responsive / pinning ─────────────────────────────────────────────────────
// A pinned drawer is an IN-FLOW column of the shell's row, so it may only pin
// where the shell IS a row: a portrait shell stacks its children
// (`useBottomNavMode().isBottomNav`), where an in-flow 100vh pane would become a
// full-width block under the page — the whole layout stretching on a toggle.
const { isBottomNav } = useBottomNavMode();

const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);
function onResize() {
  isMobile.value = window.innerWidth < 768;
}
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const effectivePinned = computed(() => !isMobile.value && !isBottomNav.value && ui.siteDrawerPinned);

const panelStyle = computed(() => {
  if (effectivePinned.value) return { width: '360px' };
  if (isMobile.value) return { top: '0', right: '0', bottom: '0', left: '0' };
  return { top: '60px', right: '16px', bottom: '16px', width: '360px' };
});

// A column host: the clock (and every other card) can size itself to the drawer
// rather than to a tile it will never have.
provide(WIDGET_LAYOUT_TYPE, 'column');

function syntheticWidget<T extends object>(type: string, config: T): DashboardWidgetLike {
  return {
    id: `site-drawer-${type}`,
    layoutId: 'site-drawer',
    widgetType: type,
    widgetConfig: config as unknown as WidgetConfigLike,
    positionX: 0,
    positionY: 0,
    colSpan: 1,
    rowSpan: 1,
    sortOrder: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/** Just the clock and its date — no month grid, agenda or world clocks. */
const CLOCK_CONFIG: TimeWidgetConfig = {
  style: 'digital',
  showDate: true,
  showSeconds: false,
  showCalendar: false,
  showEvents: false,
  showSunMoon: false,
  showWorldClocks: false,
  serverSync: false,
};

const WEATHER_CONFIG = { compact: true, showForecast: false };
const CAMERAS_CONFIG = { maxCameras: 4, columns: 2 };
const SECURITY_CONFIG = { title: tt('Security', 'Varnost') };
const LIGHTS_CONFIG = { title: tt('Lights', 'Luči') };

const clockWidget = computed(() => syntheticWidget('time', CLOCK_CONFIG));
const weatherWidget = computed(() => syntheticWidget('weather', WEATHER_CONFIG));
const camerasWidget = computed(() => syntheticWidget('camera-list', CAMERAS_CONFIG));
const securityWidget = computed(() => syntheticWidget('security-mode', SECURITY_CONFIG));
const lightsWidget = computed(() => syntheticWidget('lights-summary', LIGHTS_CONFIG));

/**
 * What a card used to receive from the board (the entity bundle + datapoints).
 * The cards read their own stores; this is the same bundle the dashboard hands
 * them, so a card behaves identically here and there.
 */
const entitiesStore = useEntitiesStore();
const dashboardStore = useDashboardStore();

const dashboardData = computed(() => ({
  devices: Array.isArray(entitiesStore.devices) ? entitiesStore.devices : [],
  rooms: Array.isArray(entitiesStore.rooms) ? entitiesStore.rooms : [],
  areas: Array.isArray(entitiesStore.areas) ? entitiesStore.areas : [],
  scenes: Array.isArray(entitiesStore.scenes) ? entitiesStore.scenes : [],
  integrations: Array.isArray(entitiesStore.integrations) ? entitiesStore.integrations : [],
  datapoints: dashboardStore.dashboardData?.datapoints || new Map(),
}));
</script>

<template>
  <!-- Backdrop (floating mode only) -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open && !effectivePinned"
        class="fixed inset-0 z-40 bg-black/30"
        data-testid="site-drawer-backdrop"
        @click="close()" />
    </Transition>
  </Teleport>

  <!-- Drawer panel: in-flow when pinned, fixed overlay when floating -->
  <Transition :name="effectivePinned ? 'drawer-push' : 'drawer-slide'">
    <div
      v-if="open"
      class="glass-surface site-drawer flex flex-col bg-white dark:bg-gray-800 overflow-hidden pt-safe pb-safe"
      :class="[
        effectivePinned
          ? 'flex-shrink-0 h-screen sticky top-0 border-l border-gray-200 dark:border-gray-700 shadow-lg'
          : 'fixed z-50 border border-gray-200 dark:border-gray-700 shadow-2xl',
        !effectivePinned && !isMobile ? 'rounded-2xl' : '',
      ]"
      :style="panelStyle"
      data-testid="site-drawer">
      <!-- Header -->
      <div
        class="glass-header flex items-center gap-2.5 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex-shrink-0 h-14">
        <div class="p-1.5 rounded-lg flex-shrink-0 bg-sky-100/80 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300">
          <span class="material-symbols-outlined" style="font-size: 18px">widgets</span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-gray-900 dark:text-white leading-tight">
            {{ tt('At a glance', 'Na pogled') }}
          </p>
          <p class="truncate text-xs text-gray-500 dark:text-gray-400">
            {{ tt('Live overview', 'Pregled v živo') }}
          </p>
        </div>
        <button
          v-if="!isMobile"
          type="button"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0"
          :title="effectivePinned ? tt('Unpin', 'Odpni') : tt('Pin', 'Pripni')"
          @click="ui.toggleSiteDrawerPin()">
          <span class="material-symbols-outlined text-lg">{{ effectivePinned ? 'push_pin' : 'keep' }}</span>
        </button>
        <button
          v-if="!effectivePinned"
          type="button"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0"
          data-testid="site-drawer-close"
          :title="tt('Close the drawer', 'Zapri predal')"
          @click="close()">
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      <!-- Cards -->
      <div class="flex-1 overflow-y-auto min-h-0 site-drawer__body" data-testid="site-drawer-body">
        <section class="site-drawer__section" data-testid="site-drawer-clock">
          <TimeCard :widget="clockWidget" :config="CLOCK_CONFIG" />
        </section>

        <section class="site-drawer__section" data-testid="site-drawer-weather">
          <WeatherCard :widget="weatherWidget" :config="WEATHER_CONFIG" />
        </section>

        <section class="site-drawer__section" data-testid="site-drawer-cameras">
          <CameraListCard :widget="camerasWidget" :config="CAMERAS_CONFIG" :dashboard-data="dashboardData" />
        </section>

        <section class="site-drawer__section" data-testid="site-drawer-security">
          <SecurityModeCard :widget="securityWidget" :config="SECURITY_CONFIG" :dashboard-data="dashboardData" />
        </section>

        <section class="site-drawer__section" data-testid="site-drawer-lights">
          <LightsSummaryCard :widget="lightsWidget" :config="LIGHTS_CONFIG" :dashboard-data="dashboardData" />
        </section>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.site-drawer__body {
  padding: 0.5rem 0.75rem 1rem;
}

.site-drawer__section + .site-drawer__section {
  margin-top: 0.25rem;
}

@media (max-width: 767px) {
  .site-drawer__body {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }
}

/* Slide/fade machinery — the same shapes the user and status drawers carry,
   scoped here so the drawer needs no global stylesheet. */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition:
    transform 200ms ease,
    opacity 200ms ease;
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* A pinned drawer changes the page's WIDTH, and a BIM page's 3D viewport is
   driven by a ResizeObserver — a width tween would resize the canvas on every
   frame of it, which reads as the model stretching. The pane therefore snaps to
   its column and only fades; the scene re-frames once, on the settled size. */
.drawer-push-enter-active,
.drawer-push-leave-active {
  transition: opacity 120ms ease;
}

.drawer-push-enter-from,
.drawer-push-leave-to {
  opacity: 0;
}
</style>
